import { useState, useMemo, useEffect, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetAllWarrantyClaimsQuery, useWarrantyClaimsApprovementMutation, useWarrantyClaimsReturnMutation, useBulkApproveSimpleMutation, useGetLookUpDropdownListQuery } from '../../../../api';
import { useGetDealerDropdownListQuery, useGetDealerCodeDropdownListQuery } from '../../../../api/endpoints/dealer';
import toastService from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

// Initial filter state - tek bir yerde tanımlanıyor
const INITIAL_FILTER_STATE = {
    dealerId: null,
    bosBranchCode: null,
    bosBranchCodeMatchMode: null,
    country: null,
    countryMatchMode: null,
    chassisNo: null,
    chassisNoMatchMode: null,
    warrClaimsNo: null,
    warrClaimsNoMatchMode: null,
    warrType: null,
    warrTypeMatchMode: null,
    totalPrice: null,
    totalPriceMatchMode: null,
    laborTotalAmount: null,
    laborTotalAmountMatchMode: null,
    otherCostTotalAmount: null,
    otherCostTotalAmountMatchMode: null
};

// localStorage'dan filtre ve sıralama bilgilerini oku
const loadFiltersFromStorage = () => {
    try {
        const storageKey = 'pendingApprovalRequestsFilters';
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            return JSON.parse(savedData);
        }
    } catch (error) {
        console.error('Error reading filters from localStorage:', error);
    }
    return null;
};

// localStorage'a filtre ve sıralama bilgilerini kaydet
const saveFiltersToStorage = (filters, sortField, sortOrder, lazyParams, dealerFilterValue, warrantyTypeFilterValue) => {
    try {
        const storageKey = 'pendingApprovalRequestsFilters';
        const dataToSave = {
            filters,
            sortField,
            sortOrder,
            lazyParams,
            dealerFilterValue: dealerFilterValue || '',
            warrantyTypeFilterValue: warrantyTypeFilterValue || ''
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
        console.error('Error saving filters to localStorage:', error);
    }
};

// localStorage'dan seçili taleplerin id listesini ve "Hepsini seç" durumunu oku
// (detay sayfasına gidip geri dönünce seçim kaybolmasın diye)
const loadSelectionFromStorage = () => {
    try {
        const storageKey = 'pendingApprovalRequestsSelection';
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            return JSON.parse(savedData);
        }
    } catch (error) {
        console.error('Error reading selection from localStorage:', error);
    }
    return null;
};

// localStorage'a seçili talep id'lerini ve "Hepsini seç" durumunu kaydet
const saveSelectionToStorage = (selectedIds, selectAllValue) => {
    try {
        const storageKey = 'pendingApprovalRequestsSelection';
        localStorage.setItem(storageKey, JSON.stringify({ selectedIds, selectAll: selectAllValue }));
    } catch (error) {
        console.error('Error saving selection to localStorage:', error);
    }
};

const usePendingApprovalRequestsData = () => {
    const { th, getCurrentLanguageId } = useLanguage();

    // İlk yükleme sırasında localStorage'dan filtre ve sıralama bilgilerini oku
    const initialStoredData = loadFiltersFromStorage();

    const [dealerSearchText, setDealerSearchText] = useState('');
    const [debouncedDealerSearch, setDebouncedDealerSearch] = useState('');
    const [dealerFilterValue, setDealerFilterValue] = useState(() => {
        return initialStoredData?.dealerFilterValue || '';
    });
    const [dealerCodeSearchText, setDealerCodeSearchText] = useState('');
    const [debouncedDealerCodeSearch, setDebouncedDealerCodeSearch] = useState('');
    const [dealerCodeFilterValue, setDealerCodeFilterValue] = useState('');
    const [warrantyTypeFilterValue, setWarrantyTypeFilterValue] = useState(() => {
        return initialStoredData?.warrantyTypeFilterValue || '';
    });

    const [lazyParams, setLazyParams] = useState(() => {
        if (initialStoredData?.lazyParams) {
            return initialStoredData.lazyParams;
        }
        return {
            first: 0,
            rows: 10,
            page: 1
        };
    });

    const [sortField, setSortField] = useState(() => {
        return initialStoredData?.sortField || null;
    });

    const [sortOrder, setSortOrder] = useState(() => {
        return initialStoredData?.sortOrder || 0;
    });

    // Filter state - localStorage'dan oku
    const [activeFilters, setActiveFilters] = useState(() => {
        if (initialStoredData?.filters) {
            return initialStoredData.filters;
        }
        return INITIAL_FILTER_STATE;
    });

    // Selected requests state - localStorage'dan oku (detay sayfasından geri dönünce seçim korunsun diye)
    const initialStoredSelection = loadSelectionFromStorage();
    const [selectedRequests, setSelectedRequests] = useState(() => {
        const ids = initialStoredSelection?.selectedIds || [];
        return ids.map((id) => ({ id }));
    });
    // "Hepsini seç" modu ve önceki seçim hafızası
    const [selectAll, setSelectAll] = useState(() => Boolean(initialStoredSelection?.selectAll));
    const preSelectAllSnapshot = useRef([]);

    const languageId = useMemo(() => getCurrentLanguageId(), [getCurrentLanguageId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDealerSearch(dealerSearchText);
        }, 500);

        return () => clearTimeout(timer);
    }, [dealerSearchText]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDealerCodeSearch(dealerCodeSearchText);
        }, 500);

        return () => clearTimeout(timer);
    }, [dealerCodeSearchText]);

    // Memoize queryParams to prevent unnecessary API calls
    const queryParams = useMemo(() => {
        const params = {
            status: 1, // Sabit: Onay Bekliyor durumu
            bosBranchCode: '',
            bosBranchSiteCode: '',
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: languageId,
            showWaitingApproval: true
        };

        if (activeFilters.dealerId && !Array.isArray(activeFilters.dealerId)) {
            params.dealerId = activeFilters.dealerId;
        }
        const filters = buildDynamicFilters(activeFilters);
        if (filters && filters.length > 0) {
            params.filters = filters;
        }
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts && sorts.length > 0) {
            params.sorts = sorts;
        }

        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, languageId, sortField, sortOrder]);

    const { data: apiPendingRequests, isLoading, isFetching, error, refetch } = useGetAllWarrantyClaimsQuery(queryParams);

    // selectAll aktifken sayfa değişince gelen yeni satırları seçiliye ekle
    useEffect(() => {
        if (!selectAll) return;
        const newItems = apiPendingRequests?.items;
        if (!newItems || newItems.length === 0) return;
        setSelectedRequests((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const toAdd = newItems.filter((r) => !existingIds.has(r.id));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
    }, [apiPendingRequests?.items, selectAll]);

    // API verisi geldiginde, sadece id ile tutulan (localStorage'dan geri yüklenen) seçili satırları tam veriyle güncelle
    useEffect(() => {
        const newItems = apiPendingRequests?.items;
        if (!newItems || newItems.length === 0) return;
        setSelectedRequests((prev) => {
            let changed = false;
            const updated = prev.map((item) => {
                const fresh = newItems.find((r) => r.id === item.id);
                if (fresh && fresh !== item) {
                    changed = true;
                    return fresh;
                }
                return item;
            });
            return changed ? updated : prev;
        });
    }, [apiPendingRequests?.items]);

    // Seçim değiştikçe localStorage'a yaz (detaya gidip geri dönünce seçim kalıcı olsun)
    useEffect(() => {
        saveSelectionToStorage(
            selectedRequests.map((r) => r.id),
            selectAll
        );
    }, [selectedRequests, selectAll]);

    const [approvementWarrantyClaims, { isLoading: isAccepting }] = useWarrantyClaimsApprovementMutation();
    const [returnWarrantyClaims, { isLoading: isReturning }] = useWarrantyClaimsReturnMutation();
    const [bulkApproveSimple, { isLoading: isBulkProcessing }] = useBulkApproveSimpleMutation();

    // Dealer dropdown için API çağrısı
    const { data: dealerList = [], isFetching: isFetchingDealers } = useGetDealerDropdownListQuery({
        RowCount: 50,
        SearchText: debouncedDealerSearch
    });

    const dealerOptionsCache = useRef(new Map());

    useEffect(() => {
        dealerList.forEach((dealer) => {
            dealerOptionsCache.current.set(dealer.dealerId, dealer.name);
        });
    }, [dealerList]);

    const dealerOptions = useMemo(() => {
        const apiOptions = dealerList.map((dealer) => ({
            label: dealer.name,
            value: dealer.dealerId
        }));
        const existingValues = new Set(apiOptions.map((opt) => opt.value));
        if (Array.isArray(activeFilters.dealerId)) {
            activeFilters.dealerId.forEach((id) => {
                if (!existingValues.has(id)) {
                    const cachedLabel = dealerOptionsCache.current.get(id);
                    if (cachedLabel) {
                        apiOptions.unshift({ label: cachedLabel, value: id });
                    }
                }
            });
        }
        return apiOptions;
    }, [dealerList, activeFilters.dealerId]);

    // Dealer code dropdown için API çağrısı
    const { data: dealerCodeList = [] } = useGetDealerCodeDropdownListQuery({
        RowCount: 100,
        SearchText: debouncedDealerCodeSearch
    });

    const dealerCodeOptionsCache = useRef(new Map());

    useEffect(() => {
        if (Array.isArray(dealerCodeList)) {
            dealerCodeList.forEach((d) => {
                dealerCodeOptionsCache.current.set(String(d.erpDealerId), String(d.sapDealerId));
            });
        }
    }, [dealerCodeList]);

    const dealerCodeOptions = useMemo(() => {
        if (!Array.isArray(dealerCodeList)) return [];
        const apiOptions = dealerCodeList.map((d) => ({
            label: String(d.sapDealerId),
            value: String(d.erpDealerId)
        }));
        const existingValues = new Set(apiOptions.map((opt) => opt.value));
        if (Array.isArray(activeFilters.bosBranchCode)) {
            activeFilters.bosBranchCode.forEach((code) => {
                if (!existingValues.has(code)) {
                    const cachedLabel = dealerCodeOptionsCache.current.get(code);
                    if (cachedLabel) {
                        apiOptions.unshift({ label: cachedLabel, value: code });
                    }
                }
            });
        }
        return apiOptions;
    }, [dealerCodeList, activeFilters.bosBranchCode]);

    // Warranty Type dropdown için API çağrısı
    const { data: warrantyTypeList = [] } = useGetLookUpDropdownListQuery({
        RowCount: 100,
        LanguageId: languageId
    });

    const { data: countryLookupList = [] } = useGetLookUpDropdownListQuery({
        LanguageId: languageId,
        GroupCode: 'COUNTRIES'
    });

    const countryOptions = useMemo(() => {
        if (!Array.isArray(countryLookupList)) return [];
        return countryLookupList.map((item) => ({
            label: item.translatedText,
            value: item.code
        }));
    }, [countryLookupList]);

    // Transform warranty type list for dropdown options
    const warrantyTypeOptions = useMemo(() => {
        return warrantyTypeList.map((type) => ({
            label: type.translatedText,
            value: type.code,
            code: type.code
        }));
    }, [warrantyTypeList]);

    const handleFilterChange = (updatedFilters) => {
        const apiFilters = {
            dealerId: Array.isArray(updatedFilters.dealerId) && updatedFilters.dealerId.length > 0 ? updatedFilters.dealerId : null,
            bosBranchCode: Array.isArray(updatedFilters.bosBranchCode) && updatedFilters.bosBranchCode.length > 0 ? updatedFilters.bosBranchCode : null,
            bosBranchCodeMatchMode: FilterMatchMode.CONTAINS,
            country: Array.isArray(updatedFilters.country) && updatedFilters.country.length > 0 ? updatedFilters.country : null,
            countryMatchMode: FilterMatchMode.EQUALS,
            chassisNo: updatedFilters.chassisNo || null,
            chassisNoMatchMode: updatedFilters.chassisNoMatchMode || null,
            warrClaimsNo: updatedFilters.warrClaimsNo || null,
            warrClaimsNoMatchMode: updatedFilters.warrClaimsNoMatchMode || null,
            warrType: updatedFilters.warrType || null,
            warrTypeMatchMode: FilterMatchMode.EQUALS, // Warranty type için her zaman EQUALS
            totalPrice: updatedFilters.totalPrice || null,
            totalPriceMatchMode: updatedFilters.totalPriceMatchMode || null,
            laborTotalAmount: updatedFilters.laborTotalAmount || null,
            laborTotalAmountMatchMode: updatedFilters.laborTotalAmountMatchMode || null,
            otherCostTotalAmount: updatedFilters.otherCostTotalAmount || null,
            otherCostTotalAmountMatchMode: updatedFilters.otherCostTotalAmountMatchMode || null
        };

        setActiveFilters(apiFilters);

        const newLazyParams = {
            first: 0,
            rows: lazyParams.rows,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(apiFilters, sortField, sortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);

        setDealerSearchText('');
        setDebouncedDealerSearch('');
        setDealerFilterValue('');
        setWarrantyTypeFilterValue('');

        setSortField(null);
        setSortOrder(0);

        const newLazyParams = {
            first: 0,
            rows: 10,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'dan filtreleri temizle
        saveFiltersToStorage(INITIAL_FILTER_STATE, null, 0, newLazyParams, '', '');
    };

    const onPage = (event) => {
        const newLazyParams = {
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        };
        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(activeFilters, sortField, sortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    const handleSortChange = (event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = getBackendColumnName(nextSortField);

        if (!backendColumn && nextSortOrder !== 0) {
            return;
        }

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);

        const newLazyParams = {
            first: 0,
            rows: lazyParams.rows,
            page: 1
        };

        setLazyParams(newLazyParams);

        // localStorage'a kaydet
        saveFiltersToStorage(activeFilters, nextSortField, nextSortOrder, newLazyParams, dealerFilterValue, warrantyTypeFilterValue);
    };

    // "Hepsini seç" toggle
    const handleSelectAll = (checked) => {
        if (checked) {
            preSelectAllSnapshot.current = selectedRequests;
            setSelectAll(true);
            const currentItems = apiPendingRequests?.items || [];
            setSelectedRequests((prev) => {
                const existingIds = new Set(prev.map((r) => r.id));
                const toAdd = currentItems.filter((r) => !existingIds.has(r.id));
                return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
            });
        } else {
            setSelectAll(false);
            setSelectedRequests(preSelectAllSnapshot.current);
            preSelectAllSnapshot.current = [];
        }
    };

    // Kabul Et butonuna tıklandığında
    const handleAccept = async () => {
        if (selectedRequests.length === 0) {
            toastService.warn(th('pendingApprovalRequests.toast.selectAtLeastOne'));
            return;
        }

        try {
            if (selectedRequests.length > 1) {
                // Birden fazla seçim: toplu onay API'si
                const result = await bulkApproveSimple({
                    claimIds: selectedRequests.map((r) => Number(r.id)),
                    isApproved: true,
                    globalComments: '',
                    alokasyon: null,
                    allClaims: selectAll
                }).unwrap();
                toastService.success(th('pendingApprovalRequests.toast.acceptSuccess'));
                setSelectedRequests([]);
                setSelectAll(false);
                preSelectAllSnapshot.current = [];
                refetch();
                return result;
            } else {
                // Tek seçim: mevcut approve endpoint
                await approvementWarrantyClaims({
                    warrantyClaimId: Number(selectedRequests[0].id),
                    isApproved: true,
                    status: 30,
                    alokasyon: null,
                    statusDescription: ''
                }).unwrap();
                toastService.success(th('pendingApprovalRequests.toast.acceptSuccess'));
                setSelectedRequests([]);
                setSelectAll(false);
                preSelectAllSnapshot.current = [];
                refetch();
            }
        } catch (error) {
            console.error('Error accepting requests:', error);
            toastService.error(th('pendingApprovalRequests.toast.acceptError'));
        }
    };

    // İade Et butonuna tıklandığında
    const handleReturn = async (statusDescription = '') => {
        if (selectedRequests.length === 0) {
            toastService.warn(th('pendingApprovalRequests.toast.selectAtLeastOne'));
            return;
        }

        try {
            if (selectedRequests.length > 1) {
                // Birden fazla seçim: toplu iade API'si
                const result = await bulkApproveSimple({
                    claimIds: selectedRequests.map((r) => Number(r.id)),
                    isApproved: false,
                    globalComments: statusDescription || '',
                    alokasyon: null,
                    allClaims: selectAll
                }).unwrap();
                toastService.success(th('pendingApprovalRequests.toast.returnSuccess'));
                setSelectedRequests([]);
                setSelectAll(false);
                preSelectAllSnapshot.current = [];
                refetch();
                return result;
            } else {
                // Tek seçim: mevcut return endpoint
                await returnWarrantyClaims({
                    warrantyClaimId: Number(selectedRequests[0].id),
                    isApproved: false,
                    status: 15,
                    alokasyon: null,
                    statusDescription: statusDescription || ''
                }).unwrap();
                toastService.success(th('pendingApprovalRequests.toast.returnSuccess'));
                setSelectedRequests([]);
                setSelectAll(false);
                preSelectAllSnapshot.current = [];
                refetch();
            }
        } catch (error) {
            console.error('Error returning requests:', error);
            toastService.error(th('pendingApprovalRequests.toast.returnError'));
        }
    };

    return {
        apiPendingRequests,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters,
        dealerOptions,
        dealerFilterValue,
        setDealerFilterValue,
        setDealerSearchText,
        dealerCodeOptions,
        dealerCodeFilterValue,
        setDealerCodeFilterValue,
        setDealerCodeSearchText,
        warrantyTypeOptions,
        countryOptions,
        warrantyTypeFilterValue,
        setWarrantyTypeFilterValue,
        selectedRequests,
        setSelectedRequests,
        selectAll,
        handleSelectAll,
        handleAccept,
        handleReturn,
        isAccepting,
        isReturning,
        isBulkProcessing,
        refetch
    };
};

export default usePendingApprovalRequestsData;

const backendColumnMap = {
    dealerId: 'DealerId',
    warrClaimsNo: 'WarrClaimsNo',
    chassisNo: 'ChassisNo',
    totalPrice: 'TotalPrice',
    laborTotalAmount: 'LaborTotalAmount',
    otherCostTotalAmount: 'OtherCostTotalAmount',
    warrType: 'WarrType',
    bosBranchCode: 'BosBranchCode',
    country: 'Country'
};

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        if (Array.isArray(value)) {
            const cleanedValues = value.map((item) => String(item ?? '').trim()).filter((item) => item !== '');
            if (cleanedValues.length === 0) return;
            const column = getBackendColumnName(field);
            if (!column) return;

            cleanedValues.forEach((singleValue) => {
                filters.push({
                    column,
                    operator: getOperatorValue(matchMode || FilterMatchMode.EQUALS),
                    value: singleValue
                });
            });
            return;
        }

        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        // Text değerleri için trim işlemi uygula (null kontrolü ile)
        let filterValue = String(value);
        if (filterValue && typeof filterValue === 'string') {
            filterValue = filterValue.trim();
        }
        // Trim sonrası boş string kontrolü
        if (filterValue === '') return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    pushFilter('dealerId', filtersState?.dealerId, FilterMatchMode.EQUALS);
    pushFilter('bosBranchCode', filtersState?.bosBranchCode, filtersState?.bosBranchCodeMatchMode);
    pushFilter('country', filtersState?.country, filtersState?.countryMatchMode);
    pushFilter('chassisNo', filtersState?.chassisNo, filtersState?.chassisNoMatchMode);
    pushFilter('warrClaimsNo', filtersState?.warrClaimsNo, filtersState?.warrClaimsNoMatchMode);
    pushFilter('warrType', filtersState?.warrType, filtersState?.warrTypeMatchMode);
    pushFilter('totalPrice', filtersState?.totalPrice, filtersState?.totalPriceMatchMode);
    pushFilter('laborTotalAmount', filtersState?.laborTotalAmount, filtersState?.laborTotalAmountMatchMode);
    pushFilter('otherCostTotalAmount', filtersState?.otherCostTotalAmount, filtersState?.otherCostTotalAmountMatchMode);

    return filters;
};
const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};
